import {
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  ShakeHandsIcon,
} from "@vericus/cadmus-icons";
import {
  AnchorLinkButton,
  Button,
  Divider,
  Pill,
  Text,
  vars,
} from "@vericus/cadmus-ui";

import { CharterFragment } from "@/generated/graphql";
import { useRootNavigate } from "@/routes";
import { formatDateFull } from "@/utils/datetime";

import { useSetupCharter } from "../hooks/use-setup-charter";
import * as styles from "./home-page.css";
import { RailCardLayoutProps } from "./rail-card-layout";

interface GroupCharterCardProps extends RailCardLayoutProps {
  /// Student's group
  groupId: string;
  /// Group charter
  charter?: CharterFragment | null;
  /// Group formation is finalised
  finalised?: boolean;
  /// Charter due date
  dueDate?: string | null;
  /**
   * Whether the work group is still being read or refreshed. Only the create
   * CTA reads it: that branch is chosen by a `charter` this component was
   * handed as null, and a refresh in flight may be about to fill it in.
   */
  workGroupPending?: boolean;
}

export function GroupCharterCard(props: GroupCharterCardProps) {
  const { layout = "wide", title } = props;
  const rootNavigate = useRootNavigate();

  const { setupCharter: handleSetupCharter, loading } = useSetupCharter(
    props.groupId
  );

  // Returns the appropriate CTA button based on charter status
  function charterCTAButton() {
    // Group formation is not finished - charter creation is locked
    if (!props.finalised) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="" size={24} />}
          onClick={handleSetupCharter}
          disabled={true}
        >
          Group charter
        </Button>
      );
    }

    // Not yet started - charter can be created
    if (!props.charter) {
      return (
        <Button
          kind="secondary"
          icon={<PlusIcon label="" size={24} />}
          onClick={handleSetupCharter}
          // Creating against a charter that is merely unfetched would resync
          // the group's existing one to a newer form version, so this waits
          // where the read-only branches below do not.
          disabled={loading || props.workGroupPending === true}
        >
          Create charter
        </Button>
      );
    }

    // Charter exists and already agreed - can be updated
    if (
      props.charter.latestAgreement &&
      !props.charter.latestAgreement.unlockedAt
    ) {
      return (
        <Button
          kind="outline"
          icon={<EyeIcon label="" size={24} />}
          onClick={() => rootNavigate(`/charter/${props.charter?.id}`)}
          disabled={loading}
        >
          View group charter
        </Button>
      );
    }

    // Default - Continue working on charter
    return (
      <Button
        kind="secondary"
        icon={<PencilIcon label="" size={24} />}
        onClick={() => rootNavigate(`/charter/${props.charter?.id}`)}
        disabled={loading}
      >
        Continue editing
      </Button>
    );
  }

  function charterStatusPill() {
    // Group formation is not finished - charter creation is locked
    if (!props.finalised) {
      return (
        <Pill color={vars.colors.yellow200} textColor={vars.colors.yellow800}>
          Group Formation In Progress
        </Pill>
      );
    }

    // Not yet started
    if (!props.charter) {
      return (
        <Pill color={vars.colors.bgrey400} textColor={vars.colors.navy400}>
          Not Yet Started
        </Pill>
      );
    }

    // Accepted
    if (
      props.charter?.latestAgreement &&
      !props.charter.latestAgreement.unlockedAt
    ) {
      return (
        <Pill color={vars.colors.successBg} textColor={vars.colors.green800}>
          Accepted
        </Pill>
      );
    }

    // Overdue
    if (
      !props.charter?.latestAgreement ||
      props.charter?.latestAgreement.unlockedAt
    ) {
      if (props.dueDate && new Date(props.dueDate) < new Date()) {
        return (
          <Pill color={vars.colors.failureBg} textColor={vars.colors.red800}>
            Overdue - Acceptance Pending
          </Pill>
        );
      }

      // In progress and not accepted yet
      if (props.dueDate) {
        return (
          <Pill color={vars.colors.yellow200} textColor={vars.colors.yellow800}>
            In Progress - Acceptance Pending
          </Pill>
        );
      }
    }
  }

  const ctaButton = charterCTAButton();

  const card = (
    <div
      className={styles.stepCard}
      data-testid="group-charter-card"
      data-layout={layout}
    >
      <div className={styles.charterCardContainer[layout]}>
        <div>
          {layout === "stacked" && title != null && title !== "" && (
            // A heading, not `div`, so this rail section joins the page's
            // h1/h2 outline (GroupCharterCard is only ever rendered within
            // `features/home`).
            <Text kind="label" as="h2" color="indigo500" bold>
              {title}
            </Text>
          )}
          <Text kind="headingFour">Group Charter</Text>
          {layout !== "stacked" && (
            <>
              <Text kind="bodyLine">
                Your assessment requires a Group Charter. You can create one
                after your group is finalised. To know more about Group
                charters and tips for creating an effective one, you can find
                resources at the below link
              </Text>
              <br />
            </>
          )}
          <div className={styles.charterStatus}>{charterStatusPill()}</div>
          {props.dueDate &&
            (layout === "stacked" || !props.charter?.latestAgreement) && (
              <Text kind="bodySm">
                <strong>Due:</strong>{" "}
                {formatDateFull(new Date(props.dueDate))}
              </Text>
            )}
          {!props.charter?.latestAgreement && (
            <>
              <Divider />
              <div className={styles.charterCardBottom}>
                <AnchorLinkButton
                  kind="primary"
                  external
                  href="https://support.cadmus.io/tips-for-creating-group-charter"
                  rightIcon={<ArrowTopRightOnSquareIcon size={24} label="" />}
                >
                  Tips for creating Group Charter
                </AnchorLinkButton>
              </div>
            </>
          )}
        </div>
        <div>{ctaButton}</div>
      </div>
    </div>
  );

  // See group-card.tsx for why `stacked` drops the timeline icon entirely.
  if (layout === "stacked") {
    return card;
  }

  return (
    <div className={styles.steps}>
      <div className={styles.stepIcon["initial"]}>
        <ShakeHandsIcon label="" size={30} />
      </div>
      {card}
    </div>
  );
}
